import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onLevel1Click);
    this.addEventListener('click', this.#onMenuClick);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onLevel1Click);
    this.removeEventListener('click', this.#onMenuClick);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Handle clicks on level 1 menu items in the drawer.
   * Mobile-nav uses a minimal path (prev/next only) to keep INP low.
   */
  #onLevel1Click = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement
      ? event.target.closest('[data-level1-link]')
      : null);

    if (!target) return;

    if (this.hasAttribute('data-mobile-nav-menu')) {
      this.#onMobileNavTabClick(event, target);
      return;
    }

    // Update active class on li elements for all menu item clicks
    const listItem = target.closest('.menu-drawer__list-item');
    if (listItem) {
      const level1Menu = listItem.closest('.menu-drawer__menu--level1');
      if (level1Menu) {
        const allListItems = level1Menu.querySelectorAll('.menu-drawer__list-item');
        allListItems.forEach((li) => {
          li.classList.remove('menu-drawer__list-item--active');
        });
        listItem.classList.add('menu-drawer__list-item--active');
      }
    }

    const clickedListItem = target.closest('li.menu-drawer__list-item');
    if (clickedListItem && !clickedListItem.closest('.menu-drawer__menu--level1')) {
      const menuContainer = clickedListItem.closest('.menu-drawer__menu');
      if (menuContainer) {
        const allListItems = menuContainer.querySelectorAll('.menu-drawer__list-item');
        allListItems.forEach((li) => {
          li.classList.remove('menu-drawer__list-item--active');
        });
        clickedListItem.classList.add('menu-drawer__list-item--active');
      }
    }

    const hasPanel = target.dataset.hasPanel === 'true';
    if (!hasPanel) return;

    event.preventDefault();

    const handle = target.dataset.handle;
    if (!handle) return;

    const group = this.querySelector(`.menu-drawer__group[data-handle="${CSS.escape(handle)}"]`);
    const groups = this.querySelectorAll('.menu-drawer__group');

    if (!(group instanceof HTMLElement)) return;

    const currentActive = this.querySelector('.menu-drawer__group--active');
    const movingForward = currentActive
      ? Array.from(groups).indexOf(group) > Array.from(groups).indexOf(currentActive)
      : true;

    groups.forEach((grp) => {
      const isActive = grp === group;
      grp.classList.toggle('menu-drawer__group--active', isActive);

      if (!isActive) {
        grp.classList.remove('menu-drawer__group--animating', 'menu-drawer__group--reverse');
      }
    });

    // Defer animation class to after paint — avoids forced reflow on the click task (INP).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        group.classList.add('menu-drawer__group--animating');
        group.classList.toggle('menu-drawer__group--reverse', !movingForward);
      });
    });
  };

  /**
   * Nobero-style mobile tabs: touch only prev/next nodes, no smooth scroll, no panel animation.
   * @param {Event} event
   * @param {HTMLElement} target
   */
  #onMobileNavTabClick(event, target) {
    const listItem = target.closest('.menu-drawer__list-item');
    const tabs = listItem?.parentElement;
    if (!(listItem instanceof HTMLElement) || !(tabs instanceof HTMLElement)) return;

    const prevItem = tabs.querySelector(':scope > .menu-drawer__list-item--active');
    if (prevItem !== listItem) {
      if (prevItem instanceof HTMLElement) {
        prevItem.classList.remove('menu-drawer__list-item--active');
        prevItem.querySelector('[data-level1-link]')?.setAttribute('aria-selected', 'false');
      }
      listItem.classList.add('menu-drawer__list-item--active');
      target.setAttribute('aria-selected', 'true');
    }

    if (target.dataset.hasPanel !== 'true') return;

    event.preventDefault();

    const handle = target.dataset.handle;
    if (!handle) return;

    const nextGroup = this.querySelector(`.menu-drawer__group[data-handle="${CSS.escape(handle)}"]`);
    if (!(nextGroup instanceof HTMLElement)) return;

    const prevGroup = this.querySelector('.menu-drawer__group--active');
    if (prevGroup === nextGroup) return;

    prevGroup?.classList.remove('menu-drawer__group--active');
    nextGroup.classList.add('menu-drawer__group--active');

    // Instant scroll after paint — smooth scroll + scrollIntoView inflate INP.
    requestYieldCallback(() => {
      const tabWidth = listItem.offsetWidth;
      const left = listItem.offsetLeft - (tabs.clientWidth - tabWidth) / 2;
      tabs.scrollTo(Math.max(0, left), 0);
    });
  }

  /**
   * Handle clicks on menu items to update active class
   * This handles all menu item clicks, not just level 1 items with panels
   */
  #onMenuClick = (event) => {
    // Level-1 items are already handled by #onLevel1Click — skip duplicate DOM work.
    if (event.target instanceof HTMLElement && event.target.closest('[data-level1-link]')) return;

    // Check if clicked element is a menu item link
    const menuItem = /** @type {HTMLElement | null} */ (event.target instanceof HTMLElement
      ? event.target.closest('.menu-drawer__menu-item')
      : null);

    if (!menuItem) return;

    // Find the parent li element
    const listItem = menuItem.closest('.menu-drawer__list-item');
    if (!listItem) return;

    // Find the parent menu container
    const menuContainer = listItem.closest('.menu-drawer__menu');
    if (!menuContainer) return;

    // Remove active class from all li elements in the same menu
    const allListItems = menuContainer.querySelectorAll('.menu-drawer__list-item');
    allListItems.forEach((li) => {
      li.classList.remove('menu-drawer__list-item--active');
    });

    // Add active class to clicked item's li
    listItem.classList.add('menu-drawer__list-item--active');
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.classList.add('menu-open');

    // Defer focus trap until after the browser paints the open drawer (web.dev INP guidance).
    requestYieldCallback(() => trapFocus(details));
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
